import React from 'react'
import { Theme, ColorTypes, useIsTheme } from '@afterpaytouch/core'
import { CONVERGENCE_NAMESPACE, NAMESPACE_SEPARATOR, THEME_COOKIE_NAME, UseCustomTranslationResponse, SetThemeRequest, LEGAL_PREFIX } from '../model'
import { useTranslation as useNextTranslation, UserConfig, SSRConfig } from 'next-i18next'
import { Trans as NextTrans } from 'next-i18next'
import { TransProps as NextTransProps } from 'react-i18next'
import { serverSideTranslations as originalServerSideTranslations } from 'next-i18next/serverSideTranslations'
import { setCookie } from 'nookies'
import { TOptions } from 'i18next'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { useCheckoutState, useCheckoutTheme, useCohort } from '../state'
import { Cohort } from '@afterpaytouch/portal-api'
import { useCallback, useEffect, useMemo } from 'react'
import { useMerchantCallback } from './merchant'

const COPY_VARIANT_NAMESPACES = [CONVERGENCE_NAMESPACE]

export const generateI18nNamespace = (input: string[]): string[] => {
  return input.flatMap((item) => [item, ...COPY_VARIANT_NAMESPACES.map((variant) => `${item}${variant}`)])
}

export const useActiveCopyVariants = (): string[] => {
  const cohort = useCohort()
  return useMemo(() => {
    const active: string[] = []
    if (cohort === Cohort.CashOnly) active.push(CONVERGENCE_NAMESPACE)
    return active
  }, [cohort])
}

export const getTranslationKeys = (key: string | string[], variants: string[] = []): string | string[] => {
  const processSingleKey = (originalKey: string): string | string[] => {
    if (variants.length === 0) {
      return originalKey
    }

    const separatorIndex = originalKey.indexOf(NAMESPACE_SEPARATOR)
    if (separatorIndex <= 0) {
      return originalKey
    }

    const translationNamespace = originalKey.slice(0, separatorIndex)
    const translationKey = originalKey.slice(separatorIndex + 1)

    const variantKeys = variants.map((variant) => `${translationNamespace}${variant}:${translationKey}`)

    return [...variantKeys, originalKey]
  }

  if (Array.isArray(key)) {
    const processedKeys = key.reduce<Array<string | string[]>>((acc, cur) => {
      const processed = processSingleKey(cur)
      return Array.isArray(processed) ? [...acc, ...processed] : [...acc, processed]
    }, [])
    return processedKeys.flat()
  } else {
    return processSingleKey(key)
  }
}

export const useTranslation = (ns?: string[]): UseCustomTranslationResponse => {
  const variants = useActiveCopyVariants()
  const { t: originalTFunction, i18n, ready } = useNextTranslation(ns)

  const enrichedT = useCallback(
    ((originalKey: string | string[], ...rest: unknown[]) => {
      const maybeOptions = rest.length > 0 ? rest[rest.length - 1] : undefined
      const options = typeof maybeOptions === 'object' && maybeOptions !== null ? (maybeOptions as TOptions & { legal?: boolean }) : undefined

      const applyLegalPrefix = (key: string): string => (options?.legal ? `${LEGAL_PREFIX}${key}` : key)
      const keyWithLegalPrefix = Array.isArray(originalKey) ? originalKey.map(applyLegalPrefix) : applyLegalPrefix(originalKey)

      const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, variants)

      const resolvedKeys =
        options?.returnObjects && Array.isArray(convergenceKeys)
          ? convergenceKeys.find((candidate) => i18n.exists(candidate, options)) ?? convergenceKeys[convergenceKeys.length - 1]
          : convergenceKeys

      return (originalTFunction as unknown as (...args: unknown[]) => unknown)(resolvedKeys, ...rest)
    }) as typeof originalTFunction,
    [variants, originalTFunction, i18n]
  )

  return useMemo(
    () => ({
      t: enrichedT,
      i18n,
      ready,
    }),
    [enrichedT, i18n, ready]
  )
}

export const serverSideTranslations = async (
  initialLocale: string,
  namespacesRequired: string[],
  configOverride?: UserConfig | null,
  extraLocales?: string[] | false
): Promise<SSRConfig> => {
  const convergenceNameSpaces = generateI18nNamespace(namespacesRequired)
  return await originalServerSideTranslations(initialLocale, convergenceNameSpaces, configOverride, extraLocales)
}

export const updateTheme = async (data: SetThemeRequest): Promise<void> => {
  try {
    setCookie(null, THEME_COOKIE_NAME, data.theme, {
      path: '/',
      secure: true,
      sameSite: 'none',
    })
  } catch (error) {
    console.error('Failed to set theme')
  }
}

export const isValidThemeValue = (value?: string): value is Theme => !isUndefinedOrEmpty(value) && Object.values(Theme).includes(value as Theme)

export const getSharedCookieDomain = (currentHost: string, targetHost: string): string | undefined => {
  if (currentHost === targetHost) {
    return undefined
  }

  const current = currentHost.split('.')
  const target = targetHost.split('.')
  const shared: string[] = []

  while (current.length > 0 && target.length > 0 && current[current.length - 1] === target[target.length - 1]) {
    shared.unshift(current.pop() as string)
    target.pop()
  }

  return shared.length >= 2 ? shared.join('.') : undefined
}

export const setThemeHandoffCookie = (theme: Theme, targetUrl: string): void => {
  try {
    const domain = getSharedCookieDomain(window.location.hostname, new URL(targetUrl).hostname)
    setCookie(null, THEME_COOKIE_NAME, theme, {
      path: '/',
      secure: true,
      sameSite: 'none',
      ...(domain !== undefined && { domain }),
    })
  } catch (error) {
    console.error('Failed to set theme handoff cookie')
  }
}

export const useSubHeadingColor = (): ColorTypes => {
  const isAPTheme = useIsTheme(Theme.AP)

  return isAPTheme ? 'Black' : 'Gray40'
}

export const isValidCohortValue = (value?: string): value is Cohort => !isUndefinedOrEmpty(value) && Object.values(Cohort).includes(value as Cohort)

export const useUpdateAfterpayJsBrand = (): void => {
  const [theme] = useCheckoutTheme()
  const { initial } = useCheckoutState()
  const { updateBrand } = useMerchantCallback()

  useEffect(() => {
    if (initial.isWindowed) {
      updateBrand(theme)?.catch(() => {})
    }
  }, [theme])
}

export const isShadowAccount = (consumerUuid: string): boolean => {
  return consumerUuid.startsWith('C_')
}

type TransProps = Omit<NextTransProps<string>, 'i18nKey' | 'context' | 'defaults' | 'tOptions'> & {
  i18nKey: string | string[]
  context?: string
  defaults?: string
  tOptions?: (TOptions & { legal?: boolean }) | Record<string, unknown>
}

export const Trans = ({ i18nKey, tOptions, ...otherProps }: TransProps): React.ReactElement => {
  const variants = useActiveCopyVariants()
  const isLegal = typeof tOptions === 'object' && tOptions !== null && 'legal' in tOptions && tOptions.legal === true

  const applyLegalPrefix = (key: string): string => (isLegal ? `${LEGAL_PREFIX}${key}` : key)
  const keyWithLegalPrefix = Array.isArray(i18nKey) ? i18nKey.map(applyLegalPrefix) : applyLegalPrefix(i18nKey)

  const convergenceKeys = getTranslationKeys(keyWithLegalPrefix, variants)

  return <NextTrans {...otherProps} i18nKey={convergenceKeys} tOptions={tOptions} />
}
